import { type components, type GenomicFeatureTypeEnum, type paths } from "./openapi";
import type { URL_ENTITY_TYPES } from "./settings.ts";
import type { MiniFamily } from "./synteny-worker/state.ts";

export type Sequence = Required<paths["/contig/{contigId}/sequence"]["get"]["parameters"]["query"]> & {
  nucleotides: string;
};

export type Feature = components["schemas"]["TrackFeatureResponse"];
export type ExtendedFeature = components["schemas"]["TrackFeatureResponse"] &
  Partial<components["schemas"]["FeatureContigLocationResponse"]>;

export type FeatureInfo = components["schemas"]["FeatureResponse"];

export type AlignmentDensity = components["schemas"]["AlignmentDensityEnum"];
export type PileupRenderingMode = components["schemas"]["PileupRenderingModeEnum"];
export type TrackText = components["schemas"]["TrackTextEnum"];

// TODO This is the rendering options supported by all the features renderers,
// which is shared between protein, gene and features. But not all have the same options.
// For instance, protein should not have inner text.
// TODO We should expand RenderingOptionsProtein from it.
export interface RenderingOptions {
  text: TrackText;
  color: string;
  synteny: boolean;
  isVisible: boolean; // This is the track panel visibility option.
}

// TODO We also should extend a BasicTrack with specific options like ExtendedMessageDataProtein
export interface Track extends RenderingOptions {
  trackType: TrackType;
  subType?: SubType;
  trackId: string;

  // group
  groupId: string;

  // UI
  worker: Worker;

  // graphics
  imageBitmap?: ImageBitmap;

  /**
   * This is the baseStart of the current imageBitmap.
   * This may be misaligned from the track group baseShift
   * This is due to rendering time, so we need to shift the bitmap by the difference between both.
   */
  baseStart?: number;

  /**
   * This is the basePerPixel of the current imageBitmap.
   * This may be different from the main zoom level.
   * So we need to scale the bitmap to paint (is needed)
   */
  basePerPixel?: number;

  // TODO not sure this is needed. We should take the imageBitmap width and height.
  // It may change in the future if we "paginate" the drawing (multiple imageBitmaps)
  width: number;
  height: number;

  // Flag set to true the first time an imageBitmap is coming back to the main
  isLoaded: boolean;
  isOrthologsLoaded: boolean;
}

export type MiniTrack = Pick<Track, "trackId" | "trackType" | "height" | "basePerPixel" | "baseStart" | "isVisible"> & {
  shiftX: number;
};
export type MiniTracks = Map<string, MiniTrack>;
export type MiniTrackGroups = Map<string, Pick<TrackGroup, "trackIds">>;

export type TrackGroups = Map<string, TrackGroup>;

export interface TrackGroup {
  groupId: string;

  // data
  organismId: number;
  organismName: string;
  contigId: number;
  contigName: string;
  trackIds: string[];

  graphics: {
    length: number;
    baseShift: number;
    isVisible: boolean;
  };
}

export type Tracks = Map<string, Track>;

export interface OrthologMember {
  orthologId: number;
  featureId: number;
  sameGenomeMemberCount?: number;
  otherGenomesMemberCount?: number;
}

export interface OrthologBox {
  orthologId: number;
  id: number; // locationId, or whatever id
  x: number;
  y: number;
  start: number;
  stop: number;
  width: number;
  height: number;
}

export interface OrthologData extends Omit<OrthologBox, "orthologId"> {
  trackId: string;
}

export interface OrthologousGroup {
  orthologId: number;
  orthologs: OrthologData[];
}

export interface CanvasCoordinate {
  x: number;
  y: number;
}

export interface AlignmentBoundingBox {
  topLeft: CanvasCoordinate;
  topRight: CanvasCoordinate;
  bottomLeft: CanvasCoordinate;
  bottomRight: CanvasCoordinate;
}
export type TrackWorkerMessageType = "imageBitmap" | "trackInfo" | "ortholog" | "error" | "hover" | "highlight";

// TODO Delete once migrated to the simplified messaging system. Was too generic.
// See specialized interface like MainThreadImageBitmapMessage
export interface WorkerMessage {
  /** FROM Track Worker. All TO Main Thread */
  Worker: {
    /**
     * Send new orthologous data from each tracks. We merge in the main thread.
     * Each zoom change (aka pileup change) will trigger an event
     */
    Ortholog: {
      trackId: string;
      orthologs: OrthologBox[];
    };
    Error: {
      name: string;
      message: string;
      statusCode: number;
      serializedError: string;
    };
  };
  SyntenicAlignmentsWorker: {
    Update: {
      orthologousGroups: OrthologousGroup[];
    };
    Render: {
      width: number;
      height: number;
      scrollX: number;
      scrollY: number;
      tracks: MiniTracks;
      trackGroups: MiniTrackGroups;
      visibleTrackIds: string[];
      basePerPixel: number;
      alignedFamily: MiniFamily | undefined;
      activeMemberId: number;
      /** "full" doesn't exist anymore. Too much alignments shown on screen */
      alignmentDensity: AlignmentDensity;
      trackType?: TrackType;
    };
  };
  AlignmentImageBitmap: {
    imageBitmap: ImageBitmap;
    scrollX: number;
    scrollY: number;
    basePerPixel: number;
  };
}

/**
 * As rendering takes time, we may have the main thread baseStart async from the imageBitmap,
 * so while waiting for the new bitmap, we move the current bitmap accordingly.
 */
export interface MainThreadImageBitmapMessage {
  type: "imageBitmap";
  imageBitmap: ImageBitmap;
  baseStart: number;
  basePerPixel: number;
  trackId: string;
}

export interface MainThreadHoverMessage extends Partial<MinimalFeatureProps> {
  type: "hover";
  trackId: string;
  orthologId?: number;
}

export interface MainThreadHighlightMessage extends Partial<MinimalFeatureProps> {
  type: "highlight";
  needToExpandPileup: boolean;
  trackId: string;
  highlightFeatureId?: number;
}

export enum InspectionPanelTabs {
  GenomeViewer = "Synteny",
  Search = "Search",
}

export enum StatusPanelTabs {
  Help = "Help",
  DevMode = "Dev Mode",
}

export interface MinimalItem {
  featureId: number;
  trackId: string;
}

export interface MinimalFeatureProps {
  id: number;
  start: number;
  stop: number;
  name?: string;
  strand?: string;
  laneIndex?: number;
  width?: number;
  height?: number;
  x?: number;
  y?: number;
}

export type CategoryResponse = components["schemas"]["CategoryResponse"];
export type ItemResponse = components["schemas"]["ItemResponse"];
export type FeatureLocation = components["schemas"]["FeatureLocationResponse"] & { type: GenomicFeatureType };
export type SearchItem = ItemResponse & { type: GenomicFeatureType; subTypeId?: number };
export type GenomicFeatureType = components["schemas"]["GenomicFeatureTypeEnum"];
export type TrackType = components["schemas"]["TrackEnum"];
export type UrlEntityType = keyof typeof URL_ENTITY_TYPES;

export interface NavigationFeature {
  id: number;
  contigId: number;
  orgId: number;
  type: GenomicFeatureTypeEnum;
  subTypeId?: number;
  start: number;
  stop: number;
}
export type SubType = components["schemas"]["FeatureTypeResponse"];
export interface TrackFeatures {
  defaultTypes: TrackType[];
  nuFeatureTypes: SubType[];
  // TODO same for aaFeatures, markers
}
// Configuration of the current view, i.e list of all loaded trackGroup with its displayed track types
export type TrackConfiguration = Map<string, TrackFeatures>;

interface StatisticBin {
  start: number;
  stop: number;
  counts: number[];
}
export interface SyntenyStatistics {
  binCountsReference: number[];
  bins: StatisticBin[];
}

// Related to zoom and range selection
export interface Range {
  start: number;
  stop: number;
}

export interface ZoomRange {
  startX: number;
  stopX: number;
}

export interface SelectionRange extends Range {
  startY: number;
  stopY: number;
}
